import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TechnicianAuthProvider } from './contexts/TechnicianAuthContext';
import TechnicianApp from './pages/technician/TechnicianApp';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Suppliers from './pages/Suppliers';
import Projects from './pages/Projects';
import ProjectFlow from './pages/ProjectFlow';
import Documents from './pages/Documents';
import Budget from './pages/Budget';
import Invoices from './pages/Invoices';
import CashFlow from './pages/CashFlow';
import PaymentConditions from './pages/PaymentConditions';
import Planning from './pages/Planning';
import Tasks from './pages/Tasks';
import Chats from './pages/Chats';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Tools from './pages/Tools';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import { startSessionRenewal } from './services/api';
import { isStandalone } from './services/push';

function AppRoutes() {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectFlow />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/economic/budget" element={<Budget />} />
        <Route path="/economic/invoices" element={<Invoices />} />
        <Route path="/economic/cashflow" element={<CashFlow />} />
        <Route path="/economic/payments" element={<PaymentConditions />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/chats" element={<Chats />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={isAdmin ? <Settings /> : <Dashboard />} />
        <Route path="/users" element={isAdmin ? <UserManagement /> : <Dashboard />} />
      </Routes>
    </MainLayout>
  );
}

export default function App() {
  const location = useLocation();

  // Las sesiones largas de la app movil se renuevan al abrirla y al volver a ella
  useEffect(() => { startSessionRenewal(); }, []);

  // El area de tecnicos es una aplicacion aparte: identidad distinta, layout
  // propio y sin acceso al gestor. Se resuelve antes que el login de usuarios
  // para que un tecnico nunca llegue a ver la pantalla de la oficina.
  if (location.pathname.startsWith('/tecnicos')) {
    // La app instalada arranca siempre en /tecnicos. Un gestor con la sesion ya
    // iniciada no tiene que pasar por la pantalla de entrada ni ver la app de
    // los tecnicos: va a sus chats, y desde el menu tiene todo el gestor. Solo
    // con la app instalada: en un navegador de escritorio, /tecnicos sigue siendo
    // la pantalla de los tecnicos aunque el gestor tenga sesion.
    const fromApp = isStandalone() || new URLSearchParams(location.search).get('source') === 'app';
    if (fromApp && localStorage.getItem('token') && !localStorage.getItem('technician_token')) {
      return <Navigate to="/chats" replace />;
    }
    return (
      <TechnicianAuthProvider>
        <TechnicianApp />
      </TechnicianAuthProvider>
    );
  }

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
